/**
 * Receipt / Document Parser
 * Parses extracted PDF text to identify and extract common fields:
 * - Party name (company / vendor)
 * - Invoice / reference number
 * - Date of document
 * - Total amount
 * - Currency
 * - Payment method
 * - Purpose / description
 * - Document type hint
 */

import { extractVendorFacts } from './vendorFacts';
import { findRangeEndDate } from './dateDisambiguate';
import { detectDocKind, KIND_LABEL } from './docKind';

export interface ParsedReceiptData {
  partyName: string;
  invoiceRef: string;
  dateOfDocument: string;   // yyyy-mm-dd
  totalAmount: string;
  currency: string;
  paymentMethod: string;
  purpose: string;
  docTypeHint: string;      // best-guess document type name
  rawText: string;          // full extracted text for reference
  // Part 2 (date disambiguation + tax + directory). All optional; '' / null when absent.
  rawDateString?: string;   // the date EXACTLY as printed (e.g. '05/06/12'), for re-interpretation
  dateSource?: 'text' | 'filename';   // dateOfDocument was read off the page, or only found in the file name
  // Handwritten date (utils/handwrittenDate.ts) — added to the stored read once the page
  // has been looked at for one, so the AI reader is asked only once per document.
  handwrittenChecked?: boolean;
  handwrittenDate?: string;          // yyyy-mm-dd, '' when the page has none
  taxType?: string;         // GST/HST/PST/QST/VAT/Sales Tax keyword, '' if none
  taxRatePercent?: string;  // e.g. '13', '' if none
  taxAmount?: string;       // e.g. '13.00', '' if none
  businessAddress?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessWebsite?: string;
  businessCategory?: string; // category_code best-guess, '' if unclear
  natureHint?: string;       // AI only: the nature (Meal, Fuel…) from the account's list, '' if unclear
  // Rest of the vendor's letterhead block (vendorFacts.ts) — public business facts only.
  businessCity?: string;
  businessState?: string;
  businessPostalCode?: string;
  businessFax?: string;
  businessMobile?: string;
  businessTaxId?: string;
  businessTaxIdLabel?: string;
  businessTaxId2?: string;
  businessTaxId2Label?: string;
}

// Capture the date EXACTLY as printed (first date-like token) so it can be
// re-interpreted by the party/country format later. Best-effort, '' if none.
export function captureRawDate(text: string): string {
  // A date range ("11/1/2025 To 11/30/2025") dates the document by its END.
  const rangeEnd = findRangeEndDate(text);
  if (rangeEnd) return rangeEnd;
  const m = (text || '').match(
    /\b\d{1,4}[/\-.]\d{1,2}[/\-.]\d{1,4}\b|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2},?\s+\d{2,4}\b|\b\d{4}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?,?\s+\d{1,2}\b|\b\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{2,4}\b/i,
  );
  if (m) return m[0];
  // An invoice form with the date in three boxes (YR | MO | DAY): "2021 07 21".
  const boxed = (text || '').match(SPACED_YMD);
  return boxed ? `${boxed[1]}-${boxed[2]}-${boxed[3]}` : '';
}

// Year, month and day printed in separate boxes of a form, so the text has only spaces
// between them. The 4-digit 20xx year first keeps this from matching phone numbers,
// quantities and prices.
const SPACED_YMD = /\b(20\d{2})[ \t]{1,4}(0[1-9]|1[0-2])[ \t]{1,4}(0[1-9]|[12]\d|3[01])\b/;

/* ── Known company / vendor names ──────────────────────────── */
const KNOWN_VENDORS: { pattern: RegExp; name: string }[] = [
  { pattern: /telus/i, name: 'TELUS' },
  { pattern: /rogers/i, name: 'Rogers' },
  // \b prefix so "Abell" (pest control) / "Campbell" don't false-match "Bell".
  { pattern: /\bbell\b\s?(canada|mobility)?/i, name: 'Bell Canada' },
  { pattern: /shaw/i, name: 'Shaw' },
  { pattern: /fido/i, name: 'Fido' },
  { pattern: /koodo/i, name: 'Koodo' },
  { pattern: /virgin\s?(mobile|plus)?/i, name: 'Virgin Plus' },
  { pattern: /amazon/i, name: 'Amazon' },
  { pattern: /walmart/i, name: 'Walmart' },
  { pattern: /costco/i, name: 'Costco' },
  { pattern: /canadian\s?tire/i, name: 'Canadian Tire' },
  { pattern: /home\s?depot/i, name: 'Home Depot' },
  { pattern: /loblaws/i, name: 'Loblaws' },
  { pattern: /shoppers\s?drug/i, name: 'Shoppers Drug Mart' },
  { pattern: /sobeys/i, name: 'Sobeys' },
  { pattern: /metro\b/i, name: 'Metro' },
  { pattern: /freshco/i, name: 'FreshCo' },
  { pattern: /no\s?frills/i, name: 'No Frills' },
  { pattern: /staples/i, name: 'Staples' },
  { pattern: /best\s?buy/i, name: 'Best Buy' },
  { pattern: /microsoft/i, name: 'Microsoft' },
  { pattern: /google/i, name: 'Google' },
  { pattern: /apple/i, name: 'Apple' },
  { pattern: /netflix/i, name: 'Netflix' },
  { pattern: /spotify/i, name: 'Spotify' },
  { pattern: /uber/i, name: 'Uber' },
  { pattern: /enbridge/i, name: 'Enbridge' },
  // Canadian utilities / energy / telecom commonly seen on bills (logos defeat OCR,
  // but the name appears as plain text in the body — match it there). Specific names
  // come BEFORE the generic /hydro/ fallback since the loop returns the first match.
  { pattern: /sask\s?energy/i, name: 'SaskEnergy' },   // \s? tolerates kerned "Sask Energy"
  { pattern: /sask\s?power/i, name: 'SaskPower' },
  { pattern: /sask\s?tel/i, name: 'SaskTel' },
  { pattern: /\benmax\b/i, name: 'ENMAX' },
  { pattern: /\bepcor\b/i, name: 'EPCOR' },
  { pattern: /\batco\b/i, name: 'ATCO' },
  { pattern: /fortis\s?(bc|alberta|ab)?/i, name: 'FortisBC' },
  { pattern: /direct\s?energy/i, name: 'Direct Energy' },
  { pattern: /union\s?gas/i, name: 'Union Gas' },
  { pattern: /manitoba\s?hydro/i, name: 'Manitoba Hydro' },
  { pattern: /bc\s?hydro/i, name: 'BC Hydro' },
  { pattern: /hydro\s?one/i, name: 'Hydro One' },
  { pattern: /\bhydro\b/i, name: 'Hydro' },
  { pattern: /td\s?(bank|canada)/i, name: 'TD Bank' },
  { pattern: /rbc\s?(royal)?/i, name: 'RBC Royal Bank' },
  { pattern: /scotiabank|scotia\s?bank/i, name: 'Scotiabank' },
  { pattern: /bmo\b/i, name: 'BMO' },
  { pattern: /cibc\b/i, name: 'CIBC' },
  { pattern: /visa/i, name: 'Visa' },
  { pattern: /mastercard/i, name: 'Mastercard' },
  { pattern: /paypal/i, name: 'PayPal' },
  { pattern: /cra|canada\s?revenue/i, name: 'Canada Revenue Agency' },
];

/* ── Currency detection ────────────────────────────────────── */
const CURRENCY_PATTERNS: { pattern: RegExp; code: string }[] = [
  { pattern: /\bCAD\b|\bCA\$|\$\s?\d.*CDN/i, code: 'CAD' },
  { pattern: /\bUSD\b|\bUS\$/i, code: 'USD' },
  { pattern: /\bEUR\b|€/i, code: 'EUR' },
  { pattern: /\bGBP\b|£/i, code: 'GBP' },
  { pattern: /\bINR\b|₹/i, code: 'INR' },
];

/* ── Payment method detection ──────────────────────────────── */
const PAYMENT_PATTERNS: { pattern: RegExp; method: string }[] = [
  { pattern: /visa/i, method: 'Visa' },
  { pattern: /mastercard|m\/c/i, method: 'Mastercard' },
  { pattern: /amex|american\s?express/i, method: 'Amex' },
  { pattern: /debit/i, method: 'Debit' },
  { pattern: /interac/i, method: 'Interac' },
  { pattern: /cash/i, method: 'Cash' },
  { pattern: /cheque|check/i, method: 'Cheque' },
  { pattern: /e-?transfer|e\s?transfer/i, method: 'E-Transfer' },
  { pattern: /paypal/i, method: 'PayPal' },
  { pattern: /credit\s?card/i, method: 'Credit Card' },
  { pattern: /wire\s?transfer/i, method: 'Wire Transfer' },
  { pattern: /pre-?authorized|PAP|auto.?pay/i, method: 'Pre-Authorized' },
];

/* ── Document type hints ───────────────────────────────────── */
const DOC_TYPE_HINTS: { pattern: RegExp; type: string }[] = [
  { pattern: /\binvoice\b/i, type: 'Purchase Invoice' },
  { pattern: /\breceipt\b/i, type: 'Receipt' },
  { pattern: /\bstatement\b/i, type: 'Statement' },
  { pattern: /\bbill\b/i, type: 'Bill' },
  { pattern: /\bcredit\s?note\b/i, type: 'Credit Note' },
  { pattern: /\bquotation\b|\bquote\b/i, type: 'Quotation' },
  { pattern: /\bpurchase\s?order\b|\bP\.?O\.?\b/i, type: 'Purchase Order' },
  { pattern: /\bcontract\b|\bagreement\b/i, type: 'Contract' },
  { pattern: /\btax\s?(return|slip|form)\b|T4|T5|T3/i, type: 'Tax Document' },
  { pattern: /\bpay\s?(stub|slip|cheque)\b/i, type: 'Pay Stub' },
  { pattern: /\bbank\s?statement\b/i, type: 'Bank Statement' },
  { pattern: /\binsurance\b/i, type: 'Insurance Document' },
  // Require "agreement"/"tenancy" context — a stray "Rental" line item (e.g. an
  // equipment-rental price on a sign-up form) must NOT brand the whole doc a lease.
  { pattern: /\blease\s+agreement\b|\brental\s+agreement\b|\btenanc(?:y|ies)\b/i, type: 'Lease Agreement' },
  { pattern: /\bmortgage\b/i, type: 'Mortgage Document' },
];

/* ── Date parsing ──────────────────────────────────────────── */
const MONTH_MAP: Record<string, string> = {
  jan: '01', january: '01', feb: '02', february: '02', mar: '03', march: '03',
  apr: '04', april: '04', may: '05', jun: '06', june: '06',
  jul: '07', july: '07', aug: '08', august: '08', sep: '09', sept: '09', september: '09',
  oct: '10', october: '10', nov: '11', november: '11', dec: '12', december: '12',
};

function parseDate(text: string): string {
  // Try common date patterns and return yyyy-mm-dd
  const patterns: { regex: RegExp; handler: (m: RegExpMatchArray) => string | null }[] = [
    // yyyy-mm-dd
    {
      regex: /\b(20\d{2})[-/.](0[1-9]|1[0-2])[-/.]([0-2]\d|3[01])\b/,
      handler: (m) => `${m[1]}-${m[2]}-${m[3]}`,
    },
    // dd-mm-yyyy or mm-dd-yyyy — prefer mm/dd/yyyy for North America
    {
      regex: /\b(0[1-9]|1[0-2])[-/.](0[1-9]|[12]\d|3[01])[-/.](20\d{2})\b/,
      handler: (m) => `${m[3]}-${m[1]}-${m[2]}`,
    },
    // Month dd, yyyy  (e.g., "December 11, 2024")
    {
      regex: /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{1,2}),?\s+(20\d{2})\b/i,
      handler: (m) => {
        const mon = MONTH_MAP[m[1].toLowerCase().substring(0, 3)];
        if (!mon) return null;
        return `${m[3]}-${mon}-${m[2].padStart(2, '0')}`;
      },
    },
    // dd Month yyyy (e.g., "11 December 2024")
    {
      regex: /\b(\d{1,2})\s+(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(20\d{2})\b/i,
      handler: (m) => {
        const mon = MONTH_MAP[m[2].toLowerCase().substring(0, 3)];
        if (!mon) return null;
        return `${m[3]}-${mon}-${m[1].padStart(2, '0')}`;
      },
    },
    // Mon dd yyyy (e.g., "Dec 11, 2024" — abbreviated)
    {
      regex: /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+(\d{1,2}),?\s+(20\d{2})\b/i,
      handler: (m) => {
        const mon = MONTH_MAP[m[1].toLowerCase()];
        if (!mon) return null;
        return `${m[3]}-${mon}-${m[2].padStart(2, '0')}`;
      },
    },
    // yyyy mm dd in separate boxes of a form (e.g. "2021 07 21" under YR | MO | DAY).
    // Last, so any properly punctuated date on the page is preferred.
    {
      regex: SPACED_YMD,
      handler: (m) => `${m[1]}-${m[2]}-${m[3]}`,
    },
  ];

  // A date range on the document wins, and its END date is the document date.
  const rangeEnd = findRangeEndDate(text);
  if (rangeEnd) {
    for (const { regex, handler } of patterns) {
      const m = rangeEnd.match(regex);
      if (m) {
        const result = handler(m);
        if (result) return result;
      }
    }
  }

  // Look for date labels first — "Date:", "Invoice Date:", "Bill Date:", etc.
  const labeledDateRegex = /(?:invoice\s*)?date\s*[:]\s*(.{8,20})/gi;
  let labelMatch;
  while ((labelMatch = labeledDateRegex.exec(text)) !== null) {
    const candidate = labelMatch[1];
    for (const { regex, handler } of patterns) {
      const m = candidate.match(regex);
      if (m) {
        const result = handler(m);
        if (result) return result;
      }
    }
  }

  // Fallback: find any date in the text
  for (const { regex, handler } of patterns) {
    const m = text.match(regex);
    if (m) {
      const result = handler(m);
      if (result) return result;
    }
  }

  return '';
}

/* ── Amount parsing ────────────────────────────────────────── */
function parseAmount(text: string): string {
  // Look for labeled amounts first — "Total:", "Amount Due:", "Balance:", "Grand Total:"
  // A bare "Amount:" (bank-transfer / e-Transfer slips: "Amount: 300.00", "Amount is here:
  // $ 462.35") comes before a bare "Balance:" — "New Balance" is the account, not the slip.
  const amountLabels = [
    /(?:total\s*(?:due|amount|charges?)?|amount\s*due|balance\s*(?:due|owing)|grand\s*total)\s*[:$]?\s*\$?\s*([\d,]+\.\d{2})/gi,
    /(?:total)\s*[:]\s*\$?\s*([\d,]+\.\d{2})/gi,
    /(?<!tax\s{0,2})\bamount\b[^\n\d$:]{0,20}:\s*\$?\s*([\d,]+\.\d{2})/gi,
    /(?<!(?:new|available|previous|opening)\s{1,2})\bbalance\s*[:$]?\s*\$?\s*([\d,]+\.\d{2})/gi,
  ];

  for (const labelRegex of amountLabels) {
    const matches = Array.from(text.matchAll(labelRegex));
    if (matches.length > 0) {
      // Use the last "total" match (usually the grand total)
      const last = matches[matches.length - 1];
      return last[1].replace(/,/g, '');
    }
  }

  // Fallback: find the largest dollar amount on the page
  const dollarAmounts = Array.from(text.matchAll(/\$\s*([\d,]+\.\d{2})/g));
  if (dollarAmounts.length > 0) {
    let largest = 0;
    let largestStr = '';
    for (const m of dollarAmounts) {
      const val = parseFloat(m[1].replace(/,/g, ''));
      if (val > largest) {
        largest = val;
        largestStr = m[1].replace(/,/g, '');
      }
    }
    if (largestStr) return largestStr;
  }

  return '';
}

/* ── Invoice / Reference number ────────────────────────────── */
function parseInvoiceRef(text: string): string {
  // Require a delimiter (:#.) after label AND capture must start with a digit or uppercase
  // and be at least 3 chars with at least one digit to avoid false matches like "ers"
  const patterns = [
    /(?:invoice|inv)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:invoice|inv)\s*[:.]?\s*#\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:reference|ref)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:order)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:receipt)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:confirmation)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:account)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:bill)\s*(?:#|no\.?|number)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
    /(?:transaction)\s*(?:id|#|no\.?)\s*[:.]?\s*([A-Z0-9][\w-]{2,20})/i,
  ];

  for (const regex of patterns) {
    const m = text.match(regex);
    if (m && m[1]) {
      // Must contain at least one digit to be a real reference number
      if (/\d/.test(m[1])) return m[1];
    }
  }

  // Fallback: look for standalone number patterns near labels
  const fallbackMatch = text.match(/(?:account|invoice|ref|bill|order)\s*[:.]?\s*(\d[\d-]{3,20})/i);
  if (fallbackMatch) return fallbackMatch[1];

  return '';
}

/* ── Party name from filename ──────────────────────────────── */
function extractPartyFromFilename(filename: string): string {
  // Try matching known vendors from the filename
  for (const { pattern, name } of KNOWN_VENDORS) {
    if (pattern.test(filename)) return name;
  }
  return '';
}

// Last-resort party name: many documents put the issuer's name as the very first
// token of the text — a letterhead/logo wordmark (e.g. "SaskEnergy"). Accept ONLY a
// clean brand-like leading token; never an email, URL, number, or a generic header
// word, so the field is left blank rather than filled with junk (user reviews it).
const HEADER_STOPWORDS = new Set([
  // generic document words
  'invoice', 'receipt', 'statement', 'bill', 'account', 'date', 'from', 'to',
  'page', 'customer', 'order', 're', 'dear', 'attention', 'attn', 'subject',
  'the', 'this', 'that', 'your', 'our', 'for', 'and', 'with',
  // bill/statement table-header words (so we skip past them to the issuer name)
  'number', 'type', 'billing', 'rate', 'days', 'present', 'previous', 'reading',
  'metric', 'usage', 'service', 'supplied', 'balance', 'amount', 'total', 'due',
  'reference', 'period', 'gas', 'delivery', 'charges', 'summary', 'detail',
]);
// Best-effort issuer name: scan the first few tokens of the (header) text and
// return the first clean brand-like word that isn't a generic/table-header word.
// Conservative — returns '' rather than guess junk; the user reviews the field.
function guessPartyFromHeader(text: string): string {
  if (!text) return '';
  const tokens = text.trim().split(/\s+/).slice(0, 6);
  for (const tok of tokens) {
    if (!/^[A-Za-z][A-Za-z&.'-]{2,29}$/.test(tok)) continue; // letters + a little brand punctuation
    if (HEADER_STOPWORDS.has(tok.toLowerCase())) continue;
    return tok;
  }
  return '';
}

/* ── Purpose / description ─────────────────────────────────── */
function parsePurpose(text: string, partyName: string, docType: string): string {
  // Build a short purpose string from what we know
  const parts: string[] = [];
  if (docType) parts.push(docType);
  if (partyName) parts.push(`from ${partyName}`);
  return parts.join(' ') || '';
}

/* ── Main parser ───────────────────────────────────────────── */
export function parseReceiptText(text: string, filename: string = ''): ParsedReceiptData {
  const result: ParsedReceiptData = {
    partyName: '',
    invoiceRef: '',
    dateOfDocument: '',
    totalAmount: '',
    currency: '',
    paymentMethod: '',
    purpose: '',
    docTypeHint: '',
    rawText: text,
    rawDateString: '',
    taxType: '',
    taxRatePercent: '',
    taxAmount: '',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    businessWebsite: '',
    businessCategory: '',
  };

  if (!text && !filename) return result;

  const combined = text + ' ' + filename;

  // 1. Party name — first check text for known vendors, then filename
  for (const { pattern, name } of KNOWN_VENDORS) {
    if (pattern.test(text)) {
      result.partyName = name;
      break;
    }
  }
  if (!result.partyName) {
    result.partyName = extractPartyFromFilename(filename);
  }
  if (!result.partyName) {
    result.partyName = guessPartyFromHeader(text);
  }

  // 2. Invoice / reference number
  result.invoiceRef = parseInvoiceRef(text);

  // 3. Date
  result.dateOfDocument = parseDate(text);
  if (result.dateOfDocument) result.dateSource = 'text';
  // Fallback: try extracting date from filename (e.g., "TELUS-43412774-2024-12-11.pdf")
  if (!result.dateOfDocument) {
    const fnDateMatch = filename.match(/(20\d{2})[-_](0[1-9]|1[0-2])[-_]([0-2]\d|3[01])/);
    if (fnDateMatch) {
      result.dateOfDocument = `${fnDateMatch[1]}-${fnDateMatch[2]}-${fnDateMatch[3]}`;
      result.dateSource = 'filename';
    }
  }

  // 4. Total amount
  result.totalAmount = parseAmount(text);

  // 5. Currency
  for (const { pattern, code } of CURRENCY_PATTERNS) {
    if (pattern.test(combined)) {
      result.currency = code;
      break;
    }
  }
  // No explicit code on the document → leave it blank. A bare "$" is not a currency
  // (CAD, USD, AUD …); the filing screen fills in the owner's BASE currency instead.

  // 6. Payment method
  for (const { pattern, method } of PAYMENT_PATTERNS) {
    if (pattern.test(text)) {
      result.paymentMethod = method;
      break;
    }
  }

  // 7. Document type hint
  // The kind of document from what it shows (utils/docKind — receipt marks, cheque,
  // e-transfer wording, deposit slip, bill…), else the older keyword list.
  const kindCode = detectDocKind(combined);
  if (kindCode) result.docTypeHint = KIND_LABEL[kindCode] || kindCode;
  else {
    for (const { pattern, type } of DOC_TYPE_HINTS) {
      if (pattern.test(combined)) {
        result.docTypeHint = type;
        break;
      }
    }
  }
  // Default: if nothing hints at a specific type, use the neutral "Other Document"
  // rather than guessing "Receipt" — a wrong guess on every unknown upload is worse
  // than an honest "Other Document" the user can refine in the Filing tab.
  if (!result.docTypeHint) {
    result.docTypeHint = 'Other Document';
  }

  // 8. Purpose
  result.purpose = parsePurpose(text, result.partyName, result.docTypeHint);

  // 9. Part 2: raw date token (for later party/country disambiguation) + tax hints.
  result.rawDateString = captureRawDate(text);
  // Vendor letterhead: address, phone, fax, email, website, registration numbers.
  const vf = extractVendorFacts(text);
  result.businessAddress = vf.address;
  result.businessCity = vf.city;
  result.businessState = vf.state;
  result.businessPostalCode = vf.postalCode;
  result.businessPhone = vf.phone;
  result.businessFax = vf.fax;
  result.businessMobile = vf.mobile;
  result.businessEmail = vf.email;
  result.businessWebsite = vf.website;
  result.businessTaxId = vf.taxId;
  result.businessTaxIdLabel = vf.taxIdLabel;
  result.businessTaxId2 = vf.taxId2;
  result.businessTaxId2Label = vf.taxId2Label;
  const taxKw = text.match(/\b(HST|GST|QST|PST|RST|VAT)\b/i) || (/\bsales\s*tax\b/i.test(text) ? ['', 'Sales Tax'] : null);
  if (taxKw) result.taxType = taxKw[1] || 'Sales Tax';
  const pct = text.match(/\b(\d{1,2}(?:\.\d{1,3})?)\s*%/);
  if (pct) result.taxRatePercent = pct[1];

  return result;
}
